import { api } from './api';

export interface StatsKennzahlen {
  aufrufe: number;
  fehler: number;
  fehlerquote: number; // 0..1
  kunden: number;
  aktiveSchluessel: number;
}

export interface StatsZeitraum {
  von: string;
  bis: string;
  vorherVon?: string;
  vorherBis?: string;
  gekuerzt: boolean;
}

export interface ZeitreihePunkt {
  zeitpunkt: string;
  aufrufe: number;
  fehler: number;
}

export interface Auffaelligkeit {
  typ: string;
  text: string;
  schluesselId: number;
}

export interface UebersichtDto {
  zeitraum: StatsZeitraum;
  kennzahlen: StatsKennzahlen;
  vorperiode: StatsKennzahlen;
  aufloesung: 'stunde' | 'tag' | 'woche';
  zeitreihe: ZeitreihePunkt[];
  auffaelligkeiten: Auffaelligkeit[];
}

export interface Mandant {
  typ: 'team' | 'user' | 'ohne';
  id: number;
  name: string;
}

export interface KundenZeile {
  mandant: Mandant;
  aufrufe: number;
  fehler: number;
  fehlerquote: number;
  schluessel: number;
  letzterZugriff: string | null;
  vorherAufrufe: number;
}

export interface SchluesselZeile {
  id: number;
  name: string;
  prefix: string;
  stufe: string;
  angelegt: string;
  letzterZugriff: string | null;
  widerrufen: string | null;
  mandant: Mandant;
  aufrufe: number;
  fehler: number;
  fehlerquote: number;
  vorherAufrufe: number;
}

export interface SchluesselDetailDto {
  kopf: Omit<SchluesselZeile, 'aufrufe' | 'fehler' | 'fehlerquote' | 'vorherAufrufe'>;
  aufloesung: 'stunde' | 'tag' | 'woche';
  zeitreihe: ZeitreihePunkt[];
  topEndpunkte: Array<{ route: string; aufrufe: number; fehler: number; dauerMsSchnitt: number; dauerMsMax: number }>;
  letzteFehler: Array<{ zeitpunkt: string; route: string; status: number }>;
  zeitraum: StatsZeitraum;
}

export interface FilterwerteDto {
  mandanten: Array<{ wert: string; name: string }>;
  schluessel: Array<{ id: number; name: string; prefix: string; widerrufen: string | null; mandantWert: string }>;
}

export interface StatsAnfrage {
  von: string;
  bis: string;
  mandanten: string[];
  schluessel: number[];
  endpunkt: string;
  nur: 'alles' | 'fehler' | 'erfolge';
}

function query(f: StatsAnfrage): string {
  const p = new URLSearchParams();
  p.set('von', f.von);
  p.set('bis', f.bis);
  if (f.mandanten.length > 0) p.set('mandanten', f.mandanten.join(','));
  if (f.schluessel.length > 0) p.set('schluessel', f.schluessel.join(','));
  if (f.endpunkt) p.set('endpunkt', f.endpunkt);
  if (f.nur !== 'alles') p.set('nur', f.nur);
  return p.toString();
}

export const getUebersicht = (f: StatsAnfrage) =>
  api.get<UebersichtDto>(`/api/admin/api-stats/uebersicht?${query(f)}`);
export const getKunden = (f: StatsAnfrage) =>
  api.get<{ zeilen: KundenZeile[]; gekuerzt: boolean; zeitraum: StatsZeitraum }>(`/api/admin/api-stats/kunden?${query(f)}`);
export const getSchluessel = (f: StatsAnfrage) =>
  api.get<{ zeilen: SchluesselZeile[]; gekuerzt: boolean; zeitraum: StatsZeitraum }>(`/api/admin/api-stats/schluessel?${query(f)}`);
export const getSchluesselDetail = (id: number, f: StatsAnfrage) =>
  api.get<SchluesselDetailDto>(`/api/admin/api-stats/schluessel/${id}?${query(f)}`);
export const getFilterwerte = () =>
  api.get<FilterwerteDto>('/api/admin/api-stats/filterwerte');

export interface WebhookStatZeile {
  id: number;
  url: string;
  ziel: string; // nur der Host, für die Tabelle
  events: string[];
  aktiv: boolean;
  stillgelegt: string | null;
  failCount: number;
  beschreibung: string | null;
  angelegt: string;
  letzteZustellung: string | null;
  mandant: Mandant;
  zustellungen: { gesamt: number; erfolgreich: number; fehlgeschlagen: number; offen: number };
}

export interface WebhookZustellungAdmin {
  id: number;
  event: string;
  status: 'pending' | 'success' | 'failed';
  attempt: number;
  responseStatus: number | null;
  responseBody: string | null;
  deliveredAt: string | null;
  createdAt: string;
  verlauf: Array<{ versuch: number; at: string; responseStatus: number | null; antwort: string | null; ok: boolean }>;
}

export const getWebhooksStats = (f: StatsAnfrage) =>
  api.get<{ zeilen: WebhookStatZeile[]; gekuerzt: boolean; zeitraum: StatsZeitraum }>(`/api/admin/api-stats/webhooks?${query(f)}`);
export const getWebhookZustellungen = (id: number) =>
  api.get<{ webhook: { id: number; url: string; ziel: string; mandant: Mandant }; zustellungen: WebhookZustellungAdmin[] }>(`/api/admin/api-stats/webhooks/${id}/zustellungen`);
export const webhookErneutSenden = (zustellungId: number) =>
  api.post<{ id: number; status: string; attempt: number; responseStatus: number | null }>(`/api/admin/api-stats/webhook-zustellungen/${zustellungId}/erneut`, {});
